import { put, call, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { errMessage } from '../../lib/apiError';
import {
  fetchCaseloadHealth,
  fetchCoachDirectory,
  fetchReviewConversation,
  fetchReviewConversations,
  deleteReviewConversation,
} from '../apis/coachConsole';
import {
  setReviewPage,
  setReviewFilters,
  setReviewThreadPage,
  openReviewThreadAction,
  getCoachDirectoryAction,
  openReviewThreadSuccess,
  openReviewThreadFailure,
  getCaseloadHealthAction,
  getCoachDirectorySuccess,
  getCoachDirectoryFailure,
  getCaseloadHealthSuccess,
  getCaseloadHealthFailure,
  getReviewConversationsAction,
  getReviewConversationsSuccess,
  getReviewConversationsFailure,
  deleteReviewConversationAction,
  deleteReviewConversationSuccess,
  deleteReviewConversationFailure,
} from '../slices/coachConsole';

const selectReview = (state) => state.coachConsole.review;

function* handleDirectory(action) {
  const attention = Boolean(action.payload?.attention);
  try {
    const response = yield call(fetchCoachDirectory, {
      ...action.payload,
      limit: attention ? 5 : 50,
    });
    yield put(getCoachDirectorySuccess({ ...response, attention }));
  } catch (error) {
    yield put(
      getCoachDirectoryFailure({ attention, error: errMessage(error, 'Could not load coaches') })
    );
  }
}

function* handleHealth() {
  try {
    const response = yield call(fetchCaseloadHealth);
    yield put(getCaseloadHealthSuccess(response));
  } catch (error) {
    yield put(getCaseloadHealthFailure(errMessage(error, 'Could not load caseload health')));
  }
}

function* handleReviewList() {
  try {
    const { filters, page } = yield select(selectReview);
    const response = yield call(fetchReviewConversations, { ...filters, page });
    yield put(
      getReviewConversationsSuccess({
        items: response?.data || [],
        total: response?.total_records || 0,
      })
    );
  } catch (error) {
    yield put(getReviewConversationsFailure(errMessage(error, 'Could not load the review list')));
  }
}

function* handleOpenThread() {
  const { id: conversationId, page } = yield select((state) => state.coachConsole.thread);
  // Closing the panel dispatches an empty payload; there is nothing to fetch.
  if (!conversationId) return;
  try {
    const response = yield call(fetchReviewConversation, conversationId, { page, limit: 50 });
    const { messages, ...conversation } = response || {};
    yield put(
      openReviewThreadSuccess({
        id: conversationId,
        page,
        conversation,
        messages: [...(messages?.data || [])].reverse(),
        total: messages?.total_records || 0,
      })
    );
  } catch (error) {
    yield put(
      openReviewThreadFailure({
        id: conversationId,
        page,
        error: errMessage(error, 'Could not load the transcript'),
      })
    );
  }
}

function* handleDelete(action) {
  try {
    yield call(deleteReviewConversation, action.payload);
    yield put(deleteReviewConversationSuccess(action.payload));
    const { page, total } = yield select(selectReview);
    const lastPage = Math.max(1, Math.ceil(total / 50));
    yield put(page > lastPage ? setReviewPage(lastPage) : getReviewConversationsAction());
  } catch (error) {
    yield put(
      deleteReviewConversationFailure(errMessage(error, 'Could not delete this conversation'))
    );
  }
}

export default function* coachConsoleSaga() {
  yield takeLatest(getCoachDirectoryAction.type, handleDirectory);
  yield takeLatest(getCaseloadHealthAction.type, handleHealth);
  yield takeLeading(deleteReviewConversationAction.type, handleDelete);
  yield takeLatest([openReviewThreadAction.type, setReviewThreadPage.type], handleOpenThread);
  yield takeLatest(
    [getReviewConversationsAction.type, setReviewFilters.type, setReviewPage.type],
    handleReviewList
  );
}
